import type { PlaceNodeRow } from "@/api/careSites/CareSitesApi";
import {
  useCareSiteDetail,
  useCreatePlaceNode,
} from "@/api/careSites/useCareSites";
import { useAuth } from "@/auth/AuthContext";
import { ACCESS_ROLE_LABEL, PLACE_NODE_KIND_LABEL, labelOf } from "@/constants/uiLabels";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { DenseRow } from "@/ui/components/DenseRow";
import { EmptyState } from "@/ui/components/EmptyState";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { Screen } from "@/ui/components/Screen";
import { SectionCard } from "@/ui/components/SectionCard";
import { SoftSection } from "@/ui/components/SoftSection";
import { StatusChip } from "@/ui/components/StatusChip";
import { TreeView } from "@/ui/components/tree/TreeView";
import { formatTimestampDE, uiErrorMessage } from "@/utils/isoDate";
import { buildTree, type TreeNode } from "@/utils/treeUtils";
import { useLocalSearchParams } from "expo-router";
import { useMemo, useState } from "react";
import { Text, View } from "react-native";

type PlaceTreeItem = PlaceNodeRow & { parentId: string | null };

/**
 * Pflegestelle – Ortsbaum, Käfig anlegen, Access-Intervalle.
 */
export default function CareSiteDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { site, nodes, access, canManage, loading, error, refetch } =
    useCareSiteDetail(id);
  const createNode = useCreatePlaceNode(id ?? "");

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [inlineParentId, setInlineParentId] = useState<string | null>(null);
  const [cageName, setCageName] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const treeItems: PlaceTreeItem[] = useMemo(
    () => nodes.map((n) => ({ ...n, parentId: n.parent_id })),
    [nodes]
  );

  const tree = useMemo(
    () =>
      buildTree(treeItems, (a, b) => {
        if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
        return a.name.localeCompare(b.name, "de");
      }),
    [treeItems]
  );

  const rootId = useMemo(
    () => nodes.find((n) => n.node_kind === "site_root")?.id ?? null,
    [nodes]
  );

  const toggleExpand = (nodeId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const addCageUnder = async (parentId: string | null, name: string) => {
    if (!id || !canManage) return;
    setMessage(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setMessage("Name erforderlich.");
      return;
    }
    try {
      await createNode.mutateAsync({
        careSiteId: id,
        parentId: parentId ?? rootId,
        name: trimmed,
        nodeKind: "cage",
        visibility: "internal",
      });
      setInlineParentId(null);
      setCageName("");
      await refetch();
      if (parentId ?? rootId) {
        setExpandedIds((prev) => new Set(prev).add(parentId ?? rootId!));
      }
    } catch (e: any) {
      setMessage(uiErrorMessage(e, "Käfig anlegen fehlgeschlagen."));
    }
  };

  const activeAccess = access.filter((a) => !a.valid_to);

  return (
    <Screen
      title={site?.name ?? "Pflegestelle"}
      scroll
      contentStyle={{ paddingBottom: theme.space.xxl }}
    >
      <View style={{ gap: theme.space.lg }}>
        {loading ? (
          <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>
            Laden…
          </Text>
        ) : null}
        {error ? (
          <Text style={{ color: theme.color.danger }}>
            {uiErrorMessage(error, "Laden fehlgeschlagen.")}
          </Text>
        ) : null}

        {site ? (
          <View
            style={{
              backgroundColor: theme.color.surface,
              borderRadius: theme.radius.md,
              borderWidth: 1,
              borderColor: theme.color.border,
              padding: theme.space.md,
              gap: theme.space.sm,
            }}
          >
            <Text
              style={{
                color: theme.color.text,
                fontSize: theme.fontSize.h2,
                fontWeight: "800",
              }}
            >
              {site.name}
            </Text>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space.xs }}>
              <StatusChip
                label={site.ownership === "private" ? "Privat" : "Organisation"}
                tone={site.ownership === "private" ? "info" : "primary"}
                size="sm"
              />
            </View>
            {!canManage ? (
              <Text
                style={{
                  color: theme.color.muted,
                  fontSize: theme.fontSize.small,
                }}
              >
                Nur grobe Orte sichtbar (keine internen Knoten).
              </Text>
            ) : null}
          </View>
        ) : null}

        <SectionCard title="Orte">
          {tree.length === 0 ? (
            <EmptyState title="Keine Orte sichtbar." />
          ) : (
            <TreeView
              nodes={tree}
              expandedIds={expandedIds}
              onToggleExpand={toggleExpand}
              onNodePress={(node) => toggleExpand(node.id)}
              onAddChild={
                canManage
                  ? (parentId) => {
                      setInlineParentId(parentId);
                      setExpandedIds((prev) => new Set(prev).add(parentId));
                    }
                  : undefined
              }
              inlineCreatingParentId={inlineParentId}
              inlineInputPlaceholder="Käfigname…"
              onInlineCreateConfirm={(parentId, value) =>
                void addCageUnder(parentId, value)
              }
              onInlineCreateCancel={() => setInlineParentId(null)}
              renderNodeContent={(node: TreeNode<PlaceTreeItem>) => (
                <View style={{ gap: 1 }}>
                  <Text
                    style={{
                      color: theme.color.text,
                      fontWeight: "600",
                      fontSize: theme.fontSize.small,
                    }}
                  >
                    {node.data.name}
                  </Text>
                  <Text
                    style={{
                      color: theme.color.muted,
                      fontSize: theme.fontSize.tiny,
                    }}
                  >
                    {PLACE_NODE_KIND_LABEL[node.data.node_kind]}
                    {node.data.visibility === "org_coarse"
                      ? " · grob"
                      : " · intern"}
                    {node.data.is_occupiable ? " · belegbar" : ""}
                  </Text>
                </View>
              )}
            />
          )}

          {canManage && rootId ? (
            <View style={{ marginTop: theme.space.md, gap: theme.space.sm }}>
              <FloatingLabelInput
                label="Neuen Käfig am Standort"
                value={cageName}
                onChangeText={setCageName}
              />
              <AppButton
                onPress={() => void addCageUnder(rootId, cageName)}
                disabled={createNode.isPending}
              >
                {createNode.isPending ? "Anlegen…" : "Käfig anlegen"}
              </AppButton>
            </View>
          ) : null}
          {message ? (
            <Text
              style={{
                marginTop: theme.space.sm,
                color: theme.color.muted,
                fontSize: theme.fontSize.small,
              }}
            >
              {message}
            </Text>
          ) : null}
        </SectionCard>

        <SoftSection title="Zugriff">
          {access.length === 0 ? (
            <EmptyState title="Keine Zugriffe sichtbar." />
          ) : (
            <View>
              {access.map((a) => {
                const isMe = a.profile_id === user?.id;
                const open = !a.valid_to;
                return (
                  <DenseRow
                    key={a.id}
                    title={`${labelOf(ACCESS_ROLE_LABEL, a.access_role)}${
                      isMe ? " (du)" : ""
                    }`}
                    meta={`ab ${formatTimestampDE(a.valid_from)}${
                      a.valid_to
                        ? ` bis ${formatTimestampDE(a.valid_to)}`
                        : ""
                    }`}
                    trailing={
                      <StatusChip
                        label={open ? "Aktiv" : "Beendet"}
                        tone={open ? "success" : "neutral"}
                        size="sm"
                      />
                    }
                  />
                );
              })}
              {activeAccess.some((a) => a.access_role === "manager") ? (
                <Text
                  style={{
                    marginTop: theme.space.sm,
                    color: theme.color.muted,
                    fontSize: theme.fontSize.tiny,
                  }}
                >
                  Inhaber-Zugriff liegt als Verwaltungs-Zeile vor.
                </Text>
              ) : null}
            </View>
          )}
        </SoftSection>
      </View>
    </Screen>
  );
}
